/**
 * CONFIGURACIÓN DEL ROUTER - VUE ROUTER 4
 *
 * Vue Router maneja la navegación en nuestra SPA (Single Page Application).
 * Incluye rutas, guards de autenticación y manejo de permisos.
 *
 * CARACTERÍSTICAS:
 * - Rutas protegidas por autenticación
 * - Guards de autorización por rol
 * - Lazy loading de componentes
 * - Redirecciones inteligentes
 * - Manejo de errores 404
 */

import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth.js'
import { useOrganization } from '@/composables/useOrganization.js'
import { USER_ROLES } from '@/utils/constants.js'

/**
 * DEFINICIÓN DE RUTAS
 *
 * Cada ruta define una URL y qué componente mostrar
 * Las rutas lazy-loaded solo se cargan cuando son necesarias
 */
const routes = [
  /**
   * RUTA RAÍZ - Redirección inteligente
   *
   * Redirige según el estado de autenticación del usuario
   */
  {
    path: '/',
    name: 'Home',
    redirect: (to) => {
      // Esta función se ejecuta cada vez que alguien visita '/'
      const authStore = useAuthStore()

      if (authStore.isAuthenticated) {
        // Usuario autenticado: redirigir al POS
        return { name: 'POS' }
      } else {
        // Usuario no autenticado: ir a login
        return { name: 'Login' }
      }
    }
  },

  /**
   * RUTA DE LOGIN
   *
   * Accesible solo para usuarios NO autenticados
   */
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginView.vue'),
    meta: {
      title: 'Iniciar Sesión',
      requiresAuth: false,
      guestOnly: true // Solo para usuarios no autenticados
    }
  },

  /**
   * RUTA DEL POS
   *
   * Accesible para usuarios autenticados con permiso de POS
   */
  {
    path: '/pos',
    name: 'POS',
    component: () => import('@/views/POSView.vue'),
    meta: {
      title: 'Punto de Venta',
      requiresAuth: true,
      requiresPermission: 'pos:use'
    }
  },

  /**
   * RUTA 404 - Página no encontrada
   *
   * Debe ir al final para capturar todas las rutas no definidas
   */
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue'),
    meta: {
      title: 'Página no encontrada'
    }
  }
]

/**
 * CREAR INSTANCIA DEL ROUTER
 *
 * createWebHistory() usa el HTML5 History API
 * Las URLs se ven como: /login, /pos (sin #)
 */
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,

  // Comportamiento del scroll al navegar
  scrollBehavior(to, from, savedPosition) {
    // Si hay posición guardada (botón atrás), restaurarla
    if (savedPosition) {
      return savedPosition
    }

    // Si hay hash (#section), ir a esa sección
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }

    // Por defecto, ir al top
    return { top: 0, behavior: 'smooth' }
  }
})

/**
 * GUARDS GLOBALES
 *
 * Se ejecutan antes de cada navegación para validar permisos
 */

/**
 * BEFORE EACH - Guard principal
 *
 * Se ejecuta antes de navegar a cualquier ruta
 */
router.beforeEach(async (to, from, next) => {
  // Obtener store de autenticación
  const authStore = useAuthStore()

  // Logging para desarrollo
  if (import.meta.env.VITE_DEBUG === 'true') {
    console.log(`🧭 Navegando de "${from.name}" a "${to.name}"`)
    console.log('👤 Usuario autenticado:', authStore.isAuthenticated)
    console.log('🔐 Rol:', authStore.userRole)
  }

  /**
   * 1. VERIFICAR AUTENTICACIÓN
   */
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    console.log('❌ Ruta requiere autenticación, redirigiendo a login')
    return next({
      name: 'Login',
      query: { redirect: to.fullPath } // Guardar destino para después del login
    })
  }

  /**
   * 2. VERIFICAR GUEST ONLY (solo para no autenticados)
   */
  if (to.meta.guestOnly && authStore.isAuthenticated) {
    console.log('↩️ Usuario ya autenticado, redirigiendo a home')
    return next({ name: 'Home' })
  }

  /**
   * 3. VERIFICAR PERMISOS ESPECÍFICOS
   */
  if (to.meta.requiresPermission) {
    const hasPermission = authStore.checkPermission(to.meta.requiresPermission)

    if (!hasPermission) {
      console.log(`❌ Sin permiso para: ${to.meta.requiresPermission}`)
      return next({ name: 'Login' })
    }
  }

  /**
   * 4. MULTI-TENANT: VERIFICAR ESTADO DE ORGANIZACIÓN
   */
  if (authStore.isAuthenticated && authStore.organization) {
    const { isActive, isTrialExpired, canAccessRoute } = useOrganization()

    // 4.1. Organización inactiva - forzar logout
    if (!isActive.value) {
      console.error('🚫 Organización inactiva, cerrando sesión')

      // Limpiar sesión
      await authStore.logout()

      return next({
        name: 'Login',
        query: {
          error: 'organization_inactive',
          message: 'Tu organización ha sido desactivada. Contacta a soporte.'
        }
      })
    }

    // 4.2. Trial expirado - bloquear acceso a POS
    if (isTrialExpired.value && to.name === 'POS') {
      console.warn('⚠️ Trial expirado, bloqueando acceso a POS')

      return next({
        name: 'Login',
        query: {
          error: 'trial_expired',
          message: 'El período de prueba ha expirado. Por favor contacta a soporte.'
        }
      })
    }

    // 4.3. Validación general de acceso a ruta
    if (!canAccessRoute(to.name)) {
      console.warn(`🚫 Acceso denegado a ruta: ${to.name}`)

      return next({
        name: 'Login',
        query: {
          error: 'access_denied',
          message: 'No tienes permiso para acceder a esta ruta.'
        }
      })
    }
  }

  /**
   * 5. CONTINUAR NAVEGACIÓN
   */
  console.log('✅ Navegación permitida')
  next()
})

/**
 * AFTER EACH - Después de cada navegación
 *
 * Se ejecuta después de completar la navegación
 */
router.afterEach((to, from) => {
  // Actualizar título de la página
  if (to.meta.title) {
    const appName = import.meta.env.VITE_APP_NAME || 'HanamiPOS'
    document.title = `${to.meta.title} - ${appName}`
  }

  // Log para desarrollo
  if (import.meta.env.VITE_DEBUG === 'true') {
    console.log(`✅ Navegación completada: ${to.name}`)
  }
})

/**
 * MANEJO DE ERRORES DE NAVEGACIÓN
 */
router.onError((error) => {
  console.error('❌ Error en navegación:', error)

  // En caso de error, ir al login como fallback
  if (error.message.includes('Loading chunk') || error.message.includes('Failed to fetch')) {
    console.log('🔄 Error de carga, reintentando...')
    window.location.reload()
  }
})

/**
 * UTILIDADES PARA COMPONENTES
 *
 * Funciones helper que pueden usar los componentes
 */

/**
 * Navegar con validación de permisos
 */
export const navigateWithPermission = (routeName, permission = null) => {
  const authStore = useAuthStore()

  // Verificar autenticación
  if (!authStore.isAuthenticated) {
    return router.push({ name: 'Login' })
  }

  // Verificar permiso específico si se proporciona
  if (permission && !authStore.checkPermission(permission)) {
    console.warn(`Sin permiso para navegar a ${routeName}: ${permission}`)
    return false
  }

  // Navegar
  return router.push({ name: routeName })
}

/**
 * Obtener la ruta de redirección después del login
 */
export const getRedirectAfterLogin = (route) => {
  // Si hay query redirect, usarlo
  if (route.query.redirect) {
    return route.query.redirect
  }

  // Por defecto, redirigir al POS
  return '/pos'
}

/**
 * Verificar si una ruta está disponible para el usuario actual
 */
export const isRouteAvailable = (routeName) => {
  const route = routes.find(r => r.name === routeName)
  if (!route) return false

  const authStore = useAuthStore()

  // Verificar autenticación
  if (route.meta.requiresAuth && !authStore.isAuthenticated) {
    return false
  }

  // Verificar permiso específico
  if (route.meta.requiresPermission) {
    return authStore.checkPermission(route.meta.requiresPermission)
  }

  return true
}

// Exportar router por defecto
export default router